import {Box, HBox} from '../../components/core/view';
import {H3Text, H5Text} from '@buncha/components/core/text';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {formatCurrency, isWeb} from '@buncha/utils/common';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {
  useGetCatalogItem,
  useGetRunForItemDetail,
  useReplacementItemDetailsReducer,
} from '../hooks/ReplacementItemDetail';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import B2BLocationInStore from '../components/ItemDetail/B2BLocationInStore';
import {BackHeaderLeft} from '@buncha/common/components/BackHeaderLeft';
import BottomButton from '@buncha/components/composites/Button/BottomButton';
import Carousal from '@buncha/components/composites/Carousal/Carousal';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import Header from '../../components/composites/Header/Header';
import If from '../../components/conditional/If';
import ItemDescription from '../components/ItemDetail/ItemDescription/ItemDescription';
import {ItemDetailsScreen} from '../navigation/types';
import ItemName from '@buncha/batchDetails/component/ItemName';
import ItemQuantityPill from '@buncha/components/composites/ItemQuantityPill/ItemQuantityPill';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import LocationInStore from '../components/ItemDetail/locationInStore';
import {Modules} from '@buncha/utils/types';
import {ReplacementItemDetailsContext} from '../context/ReplacementItemDetails';
import RequestReplacementModal from '../components/itemReplacement/requestReplacementModal';
import {RouteParamsType} from '../types/ReplacementItemDetails';
import {ScrollView} from 'react-native';
import SimilarItems from '../components/ItemDetail/SimilarItems.tsx/SimilarItems';
import {SubstitutionItemParams} from '../types/itemReplacement';
import {SubstitutionMode} from '../types/itemDetails';
import font from '@buncha/appStyles/style/font';
import {navigation} from '@buncha/config/navigation';
import {styles} from '../styles/ItemDetails';
import {useCheckItemToAdd} from '@buncha/batchDetails/hooks/ScannedAddItem';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';
import {useSubstituteItemInRun} from '../hooks/itemReplacement';

const ReplacementItemDetailsPage = () => {
  const theme = useColorThemeType();
  const [state, dispatch] = useReplacementItemDetailsReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const [getRunForItemDetail, runState] = useGetRunForItemDetail(dispatch);
  const {getCatalogItem} = useGetCatalogItem(dispatch);
  const {button} =
    useGetLocale(
      Modules.itemDetails,
      ItemDetailsScreen.ReplacementItemDetailsPage,
    ) ?? {};

  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const catalogItemId = Number(routeParams?.catalogItemId) || 0;
  const storeIndex =
    routeParams?.storeIndex ?? runState?.data?.store?.elasticSearchIndex;
  const runId = routeParams?.runId;
  const onlyView = routeParams?.onlyView;
  const quantityToPick = routeParams?.quantity || 0;
  const catalogItem = routeParams?.catalogItem;
  const itemSource = catalogItem?.itemSource;
  const orderItem = routeParams?.orderItem;
  const orderItemId = orderItem?.id || 0;
  const orderId = orderItem?.order?.id;
  const quantity =
    orderItem?.shopperItemChangesForShopper?.updatedQuantity ||
    orderItem?.quantity ||
    0;
  const indexPosition = catalogItem?.indexPosition;
  const pageLoading = state?.runDataLoading;
  const [modalOpen, setModalOpen] = useState(false);
  const storeId = routeParams?.storeId ?? runState?.data?.store?.id;
  const isB2BStore = useIsMeijerStore(storeId);
  const {checkItem, addItemCheckError} = useCheckItemToAdd();
  const {substituteItemInRun, itemSubstituteLoading, errorMessage} =
    useSubstituteItemInRun();
  const isWebMode = isWeb();
  const [showErrorModal, setSetshowErrorModal] = useState('');

  useEffect(() => {
    Analytics.eventWithProps('ItemDescriptionSubstitutionPage', {
      substituteName: catalogItem?.name,
      substituteItemId: catalogItemId,
      substituteItemPrice: catalogItem?.price,
      itemName: orderItem?.name,
      itemOrderId: orderItem?.id,
      itemPrice: orderItem?.price,
      quantity: quantityToPick,
      type: EventType.page,
      client: getClientType(isB2BStore),
      storeType: getStoreName(String(storeId)),
    });
    if (runId) getRunForItemDetail(runId);
    if (catalogItemId && storeIndex)
      getCatalogItem({
        itemId: catalogItemId,
        storeIndex,
        itemSource: 'other',
        itemIndex: 0,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorMessage || addItemCheckError)
      setSetshowErrorModal(errorMessage || addItemCheckError);
  }, [addItemCheckError, errorMessage]);

  const toggleModal = useCallback(() => {
    setModalOpen(!modalOpen);
  }, [modalOpen]);

  const topCTAHandler = useCallback(async () => {
    if (
      !catalogItem ||
      !catalogItemId ||
      !orderId ||
      !quantity ||
      !orderItemId ||
      !storeIndex
    )
      return;
    const substituteParams: SubstitutionItemParams = {
      catalogItemId: catalogItemId,
      isCustomItem: false,
      itemImageUrl: catalogItem.itemImageUrl,
      name: catalogItem.name,
      orderId: orderId,
      position: 0,
      orderItemId: orderItemId,
      price: catalogItem.price,
      quantity: quantity, // change to dynamic if required
      substitutedBy: JSON.stringify(orderItem),
      substitutionRequestParmas: {
        substitutionMode: SubstitutionMode.approvedOverCallBeforeRequestSend,
      },
      indexPosition: catalogItem.indexPosition,
      itemSource: catalogItem.itemSource,
      isPriceSensitive: Boolean(catalogItem.flags?.priceSensitive),
      isWeightSensitive: Boolean(catalogItem.flags?.weightSensitive),
      metaLoggingForKpi: !isB2BStore
        ? catalogItem?.metaLoggingForKpi
        : undefined,
      substitutionItemId: routeParams?.substitutionItemId,
    };
    if (isB2BStore) {
      const res = await checkItem(
        catalogItem?.upc,
        orderId,
        storeIndex,
        runId ?? 0,
        catalogItem?.metaLoggingForKpi,
      );
      if (res)
        navigation.navigate(ShopperScreens.ScannedAddItemPage, {
          storeIndex: storeIndex,
          runId: runId,
          orderId: orderId,
          storeId: storeId,
          plu: catalogItem?.upc,
          substituteParams: substituteParams,
          returnPage: ShopperScreens.BatchDetailsPage,
          quantity,
        });

      return;
    }
    const response = await substituteItemInRun(substituteParams);
    if (response)
      if (isWebMode)
        navigation.navigate(HomeScreens.Shopper, {
          screen: ShopperScreens.BatchDetailsPage,
          params: {runId},
        });
      else
        navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.ItemScanner,
          params: {
            orderItemId,
            storeIndex,
            runId,
            quantity,
            source: 'Replacement Item Modal',
          },
        });
  }, [
    catalogItem,
    catalogItemId,
    checkItem,
    isB2BStore,
    isWebMode,
    orderId,
    orderItem,
    orderItemId,
    quantity,
    routeParams?.substitutionItemId,
    runId,
    storeId,
    storeIndex,
    substituteItemInRun,
  ]);

  const handleOnClose = () => {
    setSetshowErrorModal('');
  };

  const getLoadingState = useMemo(
    () => pageLoading || !state?.item,
    [pageLoading, state?.item],
  );

  return (
    <ReplacementItemDetailsContext.Provider value={provider}>
      <Header
        hideBottomSafeArea
        headerRightComponent={
          <HBox
            style={[styles.header, {backgroundColor: theme?.text.blue[300]}]}>
            <If condition={!isB2BStore}>
              <BackHeaderLeft
                containerStyle={{backgroundColor: theme?.text.blue[300]}}
                iconColor={theme?.text.white[100]}
              />
              <If condition={state.item?.category}>
                <LocationInStore
                  category={state.item?.category || 'Custom Item'}
                />
              </If>
            </If>
            <If condition={isB2BStore}>
              <B2BLocationInStore
                storeId={storeId}
                location={state.item?.itemLocation}
              />
            </If>
          </HBox>
        }
        headerColor={theme?.background.blue[300]}>
        <Box style={styles.mainContainer}>
          <If condition={getLoadingState}>
            <LoaderComponents showBottomText={false} />
          </If>
          <If condition={!getLoadingState}>
            <ScrollView>
              <Box style={styles.bodyContainer}>
                <Carousal
                  containerStyle={styles.itemContent}
                  imageContainerStyle={styles.itemImage}
                  showImageCaption={false}
                  data={state?.item?.itemImageUrls?.largeImages}
                  showPagination={true}
                />
                <ItemName
                  name={state?.item?.name ?? ''}
                  size={font.FontSize.fontH3.fontSize}
                  nameStyle={styles.ItemNameTypo}
                />
                <Box style={[styles.BottomContainer]}>
                  <H3Text
                    style={[
                      styles.ItemNameTypo,
                      {color: theme?.text.blue[300]},
                    ]}>
                    {formatCurrency(state?.item?.price ?? 0)}
                  </H3Text>
                  <ItemQuantityPill quantity={quantityToPick} />
                </Box>
                <Divider />
                <ItemDescription storeIndex={storeIndex} />

                <If condition={!onlyView}>
                  <Divider />
                  <SimilarItems />
                </If>
              </Box>
            </ScrollView>
            <If condition={!onlyView}>
              <Box style={styles.replaceButtonStyle}>
                <BottomButton
                  onPress={topCTAHandler}
                  isLoading={itemSubstituteLoading}
                  loadingIndicator
                  loadingText={button?.loading}
                  title={button?.replaceItem}
                  disabled={itemSubstituteLoading}
                  style={{backgroundColor: theme?.background?.red?.[100]}}
                />
              </Box>
            </If>
            <If condition={modalOpen}>
              <RequestReplacementModal
                catalogItemId={catalogItemId}
                catalogItem={routeParams?.catalogItem}
                orderItem={routeParams?.orderItem}
                itemSource={itemSource ?? ''}
                indexPosition={indexPosition ?? 0}
                open={modalOpen}
                onClose={toggleModal}
                isB2BStore={isB2BStore}
              />
            </If>
          </If>
        </Box>
        <If condition={Boolean(showErrorModal)}>
          <AppModal
            isOpen={true}
            onClose={handleOnClose}
            topCTAHandelr={handleOnClose}
            title={'Information!'}
            hideBottomCta
            modalBody={
              <HBox style={styles.modalBody}>
                <H5Text>{showErrorModal}</H5Text>
              </HBox>
            }
            ctaTopTitle={'OK'}
            topCTAColor={theme?.background.green[100]}
            storeId={storeId}
          />
        </If>
      </Header>
    </ReplacementItemDetailsContext.Provider>
  );
};

export default React.memo(ReplacementItemDetailsPage);
